import { motion, AnimatePresence } from "framer-motion";
import { useGameStore } from "../store";
import { useState, useEffect } from "react";
import {
  Gamepad2, Users, Loader2, CheckCircle, Clock,
  Sparkles, Star, Zap, Play,
} from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { HomeLanding } from "./home/HomeLanding";

interface DemoSet {
  id: string;
  title: string;
  topic: string;
  language: string;
  difficulty?: string;
  questionCount: number;
  highscoreName?: string | null;
  highscoreValue?: number | null;
}

function KawaiiShell({ children, wide = false }: { children: React.ReactNode; wide?: boolean }) {
  // wide = layout landing (home avec grille de démos) ; default = layout flow narrow
  // (loading, lobby mobile, join form).
  const widthClass = wide ? "max-w-5xl" : "max-w-lg";
  return (
    <div className="min-h-screen font-kawaii bg-gradient-to-br from-sakura-100 via-lavender-100 to-sky-100 flex items-center justify-center p-3 relative overflow-hidden">
      {/* Grille de logos en fond (cohérence visuelle avec /auto-room) */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.08]"
        style={{
          backgroundImage: "url(/assets/bg_grid.svg)",
          backgroundSize: "200px 200px",
          backgroundRepeat: "repeat",
        }}
      />
      <div className="absolute top-[-15%] left-[-10%] w-[350px] h-[350px] rounded-full bg-sakura-200/40 blur-3xl" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[300px] h-[300px] rounded-full bg-lavender-200/40 blur-3xl" />
      <div className={`relative z-10 w-full ${widthClass} px-1`}>
        {children}
      </div>
    </div>
  );
}

// Libelles du selecteur de langue, dans la langue elle-meme : un joueur qui ne
// lit pas l'anglais doit pouvoir reconnaitre la sienne.
const JOIN_LANG_LABEL: Record<string, { code: string; label: string }> = {
  French: { code: "FR", label: "Français" },
  German: { code: "DE", label: "Deutsch" },
  English: { code: "EN", label: "English" },
  Luxembourgish: { code: "LU", label: "Lëtzebuergesch" },
  Portuguese: { code: "PT", label: "Português" },
};

export function WelcomeScreen() {
  const {
    setPlayerName,
    startGame,
    joinRoom,
    isHost,
    roomCode,
    loading,
    players,
    setPlayerReady,
    socket,
    gameStarted,
    roomError,
    startSolo,
  } = useGameStore();
  const [name, setName] = useState("");
  const [manualRoomCode, setManualRoomCode] = useState("");
  const [error, setError] = useState("");
  const [showManualJoin, setShowManualJoin] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [demos, setDemos] = useState<DemoSet[]>([]);
  const [roomLanguages, setRoomLanguages] = useState<string[]>([]);
  const [chosenLanguage, setChosenLanguage] = useState<string | null>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const roomFromUrl = searchParams.get("room");

  useEffect(() => {
    const handler = ({ hostId } = {}) => {
      if (!socket) return;
      const amHost = useGameStore.getState().isHost || socket.id === hostId;
      if (!amHost) return;
      if (!gameStarted && roomCode) {
        navigate("/spectate");
      }
    };
    socket?.on("allPlayersReady", handler);
    return () => { socket?.off("allPlayersReady", handler); };
  }, [socket, gameStarted, roomCode, navigate]);

  useEffect(() => {
    if (roomFromUrl) setError("");
  }, [roomFromUrl]);

  // Cas typique : retour browser depuis /auto-room après avoir cliqué sur une carte démo
  // (qui a créé une room côté serveur). Le store garde roomCode + isHost de cette
  // session, ce qui afficherait à tort la vue "Host" (Room: XXX, Share this code, Start
  // Game) sur la home. Si on landed sur / sans ?room= mais qu'il reste un roomCode en
  // store, on reset (déconnecte le socket aussi : la room serveur sera GC quand le
  // dernier socket part).
  useEffect(() => {
    if (!roomFromUrl && roomCode) {
      useGameStore.getState().resetGame();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch les sets de démo configurés via DEMO_SET_IDS côté serveur. Endpoint public,
  // pas besoin d'auth. Si la liste est vide ou l'endpoint indispo, la section reste cachée.
  useEffect(() => {
    fetch("/api/demos")
      .then((r) => (r.ok ? r.json() : []))
      .then((list) => Array.isArray(list) && setDemos(list))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!socket) return;
    const handler = ({ remaining }: { remaining: number }) => {
      setCountdown(remaining > 0 ? remaining : null);
    };
    socket.on("countdown", handler);
    return () => { socket.off("countdown", handler); };
  }, [socket]);

  // Langues jouables de la room. Interrogees AVANT de rejoindre : le joueur choisit
  // sa langue avant son pseudo, il n'a donc pas encore de socket dans la room.
  // Une seule langue (cas de la grande majorite des quiz) => aucun selecteur,
  // l'ecran de join reste exactement comme avant.
  useEffect(() => {
    const code = roomFromUrl || (showManualJoin && manualRoomCode.length === 6 ? manualRoomCode : null);
    if (!code) return;
    let cancelled = false;
    fetch(`/api/public/rooms/${encodeURIComponent(code.toUpperCase())}/languages`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !data?.languages) return;
        setRoomLanguages(data.languages);
        setChosenLanguage(data.languages[0] || null);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [roomFromUrl, showManualJoin, manualRoomCode]);

  const handleJoinRoom = async (code: string) => {
    if (name.trim() && code) {
      try {
        setError("");
        setPlayerName(name);
        await joinRoom(code.toUpperCase(), chosenLanguage || undefined);
      } catch {
        setError("Failed to join room. Please check the room code.");
      }
    }
  };

  const handleStart = async () => {
    if (isHost && players.length > 0) {
      try {
        setError("");
        await startGame();
      } catch {
        setError("Failed to start game. Please try again.");
      }
    } else if (isHost && players.length === 0) {
      setError("Wait for at least one player to join before starting.");
    }
  };

  const isCurrentReady = () => {
    return players.some((p) => p.id === socket?.id && p.ready);
  };

  // Aligné sur MIN_PLAYERS_FOR_COUNTDOWN côté serveur : en dessous de 2 joueurs,
  // aucun countdown n'est armé, le lobby doit le refléter.
  const isAlone = players.length < 2;

  // --- Loading ---
  if (loading) {
    return (
      <KawaiiShell>
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ type: "spring", bounce: 0.5 }}
          className="p-8 bg-white/80 backdrop-blur-xl rounded-blob shadow-kawaii-lg border-2 border-sakura-200/50 flex flex-col items-center"
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          >
            <Sparkles className="w-16 h-16 text-sakura-400 mb-4" />
          </motion.div>
          <p className="text-sakura-500 text-xl font-extrabold">Loading...</p>
        </motion.div>
      </KawaiiShell>
    );
  }

  // --- In room (mobile player) : Ready / Lobby ---
  if (roomCode && !isHost) {
    return (
      <KawaiiShell>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: "spring", bounce: 0.4 }}
          className="bg-white/80 backdrop-blur-xl rounded-blob shadow-kawaii-lg border-2 border-sakura-200/50 p-6 space-y-5"
        >
          {/* Room badge */}
          <div className="flex justify-center">
            <motion.div
              className="bg-gradient-to-r from-sakura-400 to-lavender-400 text-white px-5 py-2 rounded-full shadow-kawaii"
              animate={{ scale: [1, 1.03, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <span className="text-sm font-bold opacity-80">Room</span>
              <span className="ml-2 text-lg font-black tracking-widest">{roomCode}</span>
            </motion.div>
          </div>

          {/* Countdown */}
          <AnimatePresence>
            {countdown !== null && (
              <motion.div
                className="flex flex-col items-center bg-gradient-to-r from-star/20 to-sakura-100 rounded-kawaii p-5 border-2 border-star/30"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                transition={{ type: "spring", bounce: 0.5 }}
              >
                <div className="flex items-center gap-2 text-sakura-600 mb-2">
                  <Clock className="w-5 h-5" />
                  <span className="text-lg font-bold">Game starts in</span>
                </div>
                <motion.span
                  className="text-6xl font-black text-sakura-500 animate-countdown-pulse"
                  key={countdown}
                  initial={{ scale: 1.4, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: "spring", bounce: 0.6 }}
                >
                  {countdown}
                </motion.span>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Hint sous le badge room. Le serveur n'arme le countdown qu'à partir de
              2 joueurs : un joueur seul qui tape Ready n'enclenche plus rien, il faut
              donc lui dire ce qu'il attend et lui laisser une porte de sortie. */}
          {countdown === null && (
            isAlone && isCurrentReady() ? (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex flex-col items-center gap-3 bg-lavender-50 rounded-kawaii p-4 border-2 border-lavender-200/60"
              >
                <div className="flex items-center gap-2 text-lavender-500">
                  <motion.span
                    animate={{ rotate: 360 }}
                    transition={{ duration: 2.5, repeat: Infinity, ease: "linear" }}
                  >
                    <Loader2 className="w-5 h-5" />
                  </motion.span>
                  <span className="font-bold">Waiting for other players</span>
                </div>
                <p className="text-center text-sm font-bold text-lavender-400">
                  The countdown starts as soon as someone else joins.
                </p>
                <motion.button
                  whileHover={{ scale: 1.04 }}
                  whileTap={{ scale: 0.96 }}
                  onClick={() => startSolo()}
                  className="w-full py-3 px-5 rounded-xl font-black bg-white text-lavender-500 border-2 border-lavender-300 shadow-kawaii-card flex items-center justify-center gap-2"
                >
                  <Play className="w-5 h-5" />
                  Start solo now
                </motion.button>
              </motion.div>
            ) : (
              <motion.p
                className="text-center text-lavender-400 font-bold"
                animate={{ opacity: [0.5, 1, 0.5] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                {isAlone
                  ? "Tap Ready. The race starts once another player joins."
                  : "Tap Ready to start the countdown!"}
              </motion.p>
            )
          )}

          {/* Ready button */}
          <motion.button
            whileHover={{ scale: 1.05, y: -3 }}
            whileTap={{ scale: 0.93 }}
            onClick={() => setPlayerReady()}
            disabled={isCurrentReady() || loading}
            className={`w-full py-4 px-6 rounded-xl font-black text-lg shadow-kawaii-lg transition-all flex items-center justify-center gap-3 ${
              isCurrentReady()
                ? "bg-mint/30 text-emerald-600 border-2 border-mint"
                : "bg-gradient-to-r from-sakura-400 via-sakura-500 to-lavender-400 text-white"
            } disabled:opacity-60 disabled:cursor-not-allowed`}
          >
            {isCurrentReady() ? (
              <>
                <CheckCircle className="w-6 h-6" />
                Ready! Waiting...
              </>
            ) : (
              <>
                <Zap className="w-6 h-6" />
                Ready!
              </>
            )}
          </motion.button>

          {/* Players list */}
          <div>
            <h3 className="text-lg font-extrabold text-lavender-500 flex items-center gap-2 mb-3">
              <Users className="w-5 h-5" />
              Players
              <span className="ml-auto bg-lavender-100 text-lavender-500 px-3 py-0.5 rounded-full text-sm">
                {players.length}
              </span>
            </h3>
            <div className="space-y-2">
              <AnimatePresence>
                {players.map((p, i) => (
                  <motion.div
                    key={p.id}
                    initial={{ opacity: 0, x: -20, scale: 0.8 }}
                    animate={{ opacity: 1, x: 0, scale: 1 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ type: "spring", bounce: 0.5, delay: i * 0.08 }}
                    className="bg-white p-3 rounded-xl flex items-center justify-between shadow-kawaii-card border-2 border-lavender-100/50"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-sakura-300 to-lavender-300 flex items-center justify-center text-white font-black text-sm shadow-sm">
                        {p.name.charAt(0).toUpperCase()}
                      </div>
                      <span className="font-bold text-gray-700 text-sm">{p.name}</span>
                    </div>
                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${
                      p.ready
                        ? "bg-mint/20 text-emerald-600"
                        : "bg-gray-100 text-gray-400"
                    }`}>
                      {p.ready ? "Ready!" : "Waiting"}
                    </span>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
      </KawaiiShell>
    );
  }

  // --- Home screen (join / create) ---
  // isHomeState = on est sur la landing pure (header + démos + footer), pas dans un flow
  // de join/host. Conditionne aussi la largeur du shell (wide pour la grille 3x3).
  const isHomeState = !roomCode && !roomFromUrl && !showManualJoin;
  // La landing a besoin de toute la largeur (bandeau 3D pleine page) : elle vit
  // dans son propre composant plutot que dans le shell centre de ce fichier.
  if (isHomeState) return <HomeLanding demos={demos} />;

  return (
    <KawaiiShell>
      {/* Logo du flow non-home (join via QR, saisie manuelle, lobby) */}
      <motion.div
          className="flex justify-center mb-6"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ type: "spring", bounce: 0.5 }}
        >
        <img src="/assets/logo.svg" alt="Logo" className="w-48 h-auto drop-shadow-lg animate-float" />
      </motion.div>

      {/* Carte du flow actif : join via QR, saisie manuelle du code, ou lobby. */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ type: "spring", bounce: 0.4, delay: 0.15 }}
        className="p-7 bg-white/80 backdrop-blur-xl rounded-blob shadow-kawaii-lg border-2 border-sakura-200/50"
      >
        {/* Icon */}
        <div className="flex justify-center mb-5">
          {roomFromUrl || showManualJoin ? (
            <motion.div animate={{ rotate: [0, -5, 5, 0] }} transition={{ duration: 2, repeat: Infinity }}>
              <Users className="w-14 h-14 text-lavender-400" />
            </motion.div>
          ) : (
            <motion.div animate={{ y: [0, -5, 0] }} transition={{ duration: 2, repeat: Infinity }}>
              <Gamepad2 className="w-14 h-14 text-sakura-400" />
            </motion.div>
          )}
        </div>

        {roomCode && (
          <h1 className="text-2xl font-black text-center text-sakura-500 mb-6">
            Room: {roomCode}
          </h1>
        )}

        {/* Error — roomError vient du serveur (code inconnu / QR périmé). Avant, cet
            échec était silencieux et le joueur croyait être dans le lobby. */}
        {(error || roomError) && (
          <motion.div
            initial={{ x: -10 }}
            animate={{ x: [0, -5, 5, -3, 3, 0] }}
            transition={{ duration: 0.4 }}
            className="bg-red-50 border-2 border-red-200 text-red-600 px-4 py-3 rounded-xl mb-4 font-bold text-sm text-center"
          >
            {error ||
              "This room is no longer available. Scan the QR code on the screen again."}
          </motion.div>
        )}

        {/* Join form */}
        {(roomFromUrl || showManualJoin) && !roomCode && (
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-5"
          >
            {/* Selecteur de langue : affiche UNIQUEMENT si le quiz existe en
                plusieurs langues. Place avant le pseudo, c'est le premier choix
                a faire. */}
            {roomLanguages.length > 1 && (
              <div>
                <label className="block text-sm font-bold text-lavender-500 mb-2">
                  Your language
                </label>
                <div className="flex flex-wrap gap-2">
                  {roomLanguages.map((lang) => {
                    const meta = JOIN_LANG_LABEL[lang] || { code: lang.slice(0, 2).toUpperCase(), label: lang };
                    const active = chosenLanguage === lang;
                    return (
                      <motion.button
                        key={lang}
                        type="button"
                        whileTap={{ scale: 0.95 }}
                        onClick={() => setChosenLanguage(lang)}
                        className={`flex-1 min-w-[90px] px-3 py-3 rounded-xl font-black border-2 transition-all ${
                          active
                            ? "bg-gradient-to-r from-sakura-400 to-lavender-400 text-white border-transparent shadow-kawaii"
                            : "bg-white/70 text-lavender-500 border-lavender-200 hover:border-sakura-300"
                        }`}
                      >
                        <span className="block text-xs opacity-75 tracking-widest">{meta.code}</span>
                        <span className="block text-sm">{meta.label}</span>
                      </motion.button>
                    );
                  })}
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-bold text-lavender-500 mb-2">
                Your nickname
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={10}
                className="w-full px-4 py-3 border-2 border-sakura-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-sakura-300 focus:border-sakura-400 font-bold text-gray-700 bg-white/70 placeholder:text-sakura-200 transition-all"
                placeholder="e.g. Sakura"
              />
            </div>

            {showManualJoin && (
              <div>
                <label className="block text-sm font-bold text-lavender-500 mb-2">
                  Room Code
                </label>
                <input
                  type="text"
                  value={manualRoomCode}
                  onChange={(e) => setManualRoomCode(e.target.value.toUpperCase())}
                  className="w-full px-4 py-3 border-2 border-lavender-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-lavender-300 focus:border-lavender-400 font-black text-2xl tracking-widest text-center text-lavender-500 bg-white/70 placeholder:text-lavender-200 transition-all"
                  placeholder="ABC123"
                  maxLength={6}
                />
              </div>
            )}

            <motion.button
              whileHover={{ scale: 1.04, y: -2 }}
              whileTap={{ scale: 0.96 }}
              onClick={() => handleJoinRoom(showManualJoin ? manualRoomCode : roomFromUrl!)}
              disabled={!name.trim() || (showManualJoin && manualRoomCode.length !== 6)}
              className="w-full py-4 px-6 text-white font-black text-lg bg-gradient-to-r from-sakura-400 via-sakura-500 to-lavender-400 rounded-xl shadow-kawaii-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <Star className="w-5 h-5" />
              GO !
            </motion.button>

            {showManualJoin && (
              <button
                onClick={() => setShowManualJoin(false)}
                className="w-full text-lavender-400 hover:text-sakura-400 font-bold transition-colors text-sm"
              >
                Back to Home
              </button>
            )}
          </motion.div>
        )}

        {/* Host view */}
        {roomCode && isHost && (
          <div className="space-y-5">
            <div className="bg-gradient-to-r from-sakura-50 to-lavender-50 p-4 rounded-kawaii border-2 border-sakura-100 text-center">
              <p className="text-sm font-bold text-lavender-400">Share this code</p>
              <p className="text-3xl font-black text-sakura-500 tracking-widest mt-1">{roomCode}</p>
            </div>
            <motion.button
              whileHover={{ scale: 1.04, y: -2 }}
              whileTap={{ scale: 0.96 }}
              onClick={handleStart}
              className="w-full py-4 px-6 text-white font-black text-lg bg-gradient-to-r from-sakura-400 via-sakura-500 to-lavender-400 rounded-xl shadow-kawaii-lg transition-all flex items-center justify-center gap-2"
            >
              <Zap className="w-5 h-5" />
              Start Game!
            </motion.button>
          </div>
        )}
      </motion.div>

    </KawaiiShell>
  );
}
